import React, { useState, useEffect, useMemo, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../context/AuthContext";
import { useCart } from "../../../context/CartContext";
import { validateCheckoutInput } from "../../../utils/CheckoutErrorHandler";
import {
  initiateGuestOrder,
  createWalletOrder,
  initiateUserStripeOrder,
  initiateGuestMultipleOrder,
  createWalletMultipleOrder,
  initiateUserStripeMultipleOrder,
} from "../../../services/OrderService";
import { markNewsletter } from "../../../services/UserService";
import { validateVoucher as validateVoucherApi } from "../../../services/VoucherService";
import EsimCheckModal from "../../Offers/EsimCheckModal/EsimCheckModal";
import { purchaseTopUp } from "../../../services/TopUpService";
import { checkPromocodeAvailability } from "../../../services/PromocodeService";

import { fetchPackageById } from "../../../services/PackagesService";

import AuthPanel from "../AuthPanel/AuthPanel";
import OrderSummary from "../OrderSummary/OrderSummary";
import PaymentModal from "../PaymentModal/PaymentModal";
import StatusModal from "../StatusModal/StatusModal";
import "../CheckoutPanel/CheckoutPanel.css";
import { logEvent } from "../../../analytics/analytics";

const CheckoutLayout = ({ status }) => {
  const navigate = useNavigate();
  const { items, clear } = useCart();
  const { token, wallet, walletCurrency, loading: authLoading, newsletter, userId } = useContext(AuthContext);
  const activeCurrency = walletCurrency || "EUR";
  const isLoggedIn = !!token;

  const [newsletterOptInGuest, setNewsletterOptInGuest] = useState(false);
  const showNewsletterCheckbox = !isLoggedIn || !newsletter;
  const [showEsimModal, setShowEsimModal] = useState(false);

  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [esimConfirmed, setEsimConfirmed] = useState(false);
  const [speedConfirmed, setSpeedConfirmed] = useState(false);
  const [newsletterOptIn, setNewsletterOptIn] = useState(false);
  const [packages, setPackages] = useState([]);
  const [orderLoading, setOrderLoading] = useState(false);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [promocodeData, setPromocodeData] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [finalStatus, setFinalStatus] = useState(status);
  const [promocodeError, setPromocodeError] = useState("");
  const [frozenItems, setFrozenItems] = useState([]);

  useEffect(() => {
    if (finalStatus !== "success" && items.length > 0) {
      setFrozenItems(items);
    }
  }, [items, finalStatus]);

  const displayItems = finalStatus === "success" ? frozenItems : items;
  const totalUnits = displayItems.reduce((sum, item) => sum + (item.quantity || 1), 0);
  const hasMultiplePackages = totalUnits > 1 || displayItems.length > 1;

  useEffect(() => {
    if (status === "success") {
      setFinalStatus("success");
      clear();
    }

    if (status === "success" || status === "canceled") {
      setShowStatusModal(true);
    }
  }, [status, clear]);

  useEffect(() => {
    if (status) setFinalStatus(status);
  }, [status]);

  useEffect(() => {
    if (finalStatus === "success" || finalStatus === "canceled") {
      setShowStatusModal(true);
    }
  }, [finalStatus]);

  const isTopUpOrder = useMemo(() => {
    return items.some((item) => item.type === "topup");
  }, [items]);

  // const packageIds = useMemo(() => {
  //   const ids = (displayItems || []).filter((it) => it.type !== "topup").map((it) => it.internal_package_id || it.id);
  //   return Array.from(new Set(ids.map((x) => String(x))));
  // }, [displayItems]);

  const packageIds = useMemo(() => {
    console.log("🔍 RAW displayItems:", displayItems);
    const ids = (displayItems || []).map((it) => {
      const id = it.internal_package_id || it.id;
      console.log(
        `📦 Item "${it.name || "topup"}": internal_package_id=${it.internal_package_id}, id=${it.id} → "${id}"`,
      );
      return id;
    });
    const stringIds = ids.map((x) => String(x));
    console.log("📋 FINAL packageIds:", stringIds);
    return Array.from(new Set(stringIds));
  }, [displayItems]);

  const currency = (value, cur = walletCurrency) => {
    if (value == null) return "";
    return `${value.toFixed(2)} ${cur}`;
  };

  const validationError = useMemo(() => {
    const name = (fullName || "").trim();
    const mail = (email || "").trim();
    return !isLoggedIn ? validateCheckoutInput(name, mail) : null;
  }, [fullName, email, isLoggedIn]);

  const handleGuestOrder = async () => {
    try {
      if (isTopUpOrder) {
        setErrorMessage("Top-Up purchases require login. Please sign in.");
        return;
      }

      setOrderLoading(true);
      let orderData;

      if (hasMultiplePackages) {
        orderData = await initiateGuestMultipleOrder({
          items: displayItems,
          userId: email,
          frontendUrl: `${window.location.origin}/cart`,
          currency: activeCurrency,
          newsletter: newsletterOptInGuest,
        });
      } else {
        const cartItem = displayItems[0];
        const internalPackageId = cartItem.internal_package_id || cartItem.id;

        orderData = await initiateGuestOrder({
          internalPackageId: internalPackageId,
          userId: email,
          frontendUrl: `${window.location.origin}/cart`,
          currency: activeCurrency,
          newsletter: newsletterOptInGuest,
        });
      }

      const stripeUrl = orderData.redirect_url;
      if (stripeUrl) {
        window.location.href = stripeUrl;
      }
    } catch (error) {
      console.error("❌ Error:", error);
      setErrorMessage(error.response?.data?.detail || "Payment failed");
    } finally {
      setOrderLoading(false);
    }
  };

  const handleWalletOrder = async () => {
    if (!wallet?.id) {
      setErrorMessage("Wallet not found");
      setFinalStatus("canceled");
      return false;
    }

    try {
      setOrderLoading(true);

      if (isTopUpOrder) {
        const cartItem = displayItems[0];

        await purchaseTopUp({
          iccid: cartItem.target_iccid,
          walletId: wallet.id,
          packageId: cartItem.internal_package_id,
          packageCode: cartItem.package_code,
        });
      } else {
        if (hasMultiplePackages) {
          await createWalletMultipleOrder({
            items: displayItems,
            walletId: wallet.id,
            promocode: promocodeData ? promocodeData.code : null,
          });
        } else {
          const cartItem = displayItems[0];
          const internalPackageId = cartItem.internal_package_id || cartItem.id;

          await createWalletOrder({
            internalPackageId: internalPackageId,
            walletId: wallet.id,
            promocode: promocodeData ? promocodeData.code : null,
          });
        }
      }

      setFinalStatus("success");
      return true;
    } catch (error) {
      console.error("❌ Wallet payment failed:", error);
      setErrorMessage(error.response?.data?.detail || "Payment failed");
      setFinalStatus("canceled");
      return false;
    } finally {
      setOrderLoading(false);
    }
  };

  const handleUserStripeOrder = async () => {
    try {
      setOrderLoading(true);
      let orderData;

      if (hasMultiplePackages) {
        orderData = await initiateUserStripeMultipleOrder({
          items: displayItems,
          walletId: wallet?.id,
          frontendUrl: `${window.location.origin}/cart`,
          promocode: promocodeData ? promocodeData.code : null,
        });
      } else {
        const cartItem = displayItems[0];
        const internalPackageId = cartItem.internal_package_id || cartItem.id;

        orderData = await initiateUserStripeOrder({
          internalPackageId: internalPackageId,
          walletId: wallet?.id,
          frontendUrl: `${window.location.origin}/cart`,
          promocode: promocodeData ? promocodeData.code : null,
        });
      }

      const stripeUrl = orderData.redirect_url;
      if (stripeUrl) {
        window.location.href = stripeUrl;
      }
    } catch (error) {
      console.error("❌ User Stripe order failed:", error);
      setErrorMessage(error.response?.data?.detail || "Payment failed");
    } finally {
      setOrderLoading(false);
    }
  };

  const handlePlaceOrder = () => {
    if (!isLoggedIn && isTopUpOrder) {
      setErrorMessage("Top-Up purchases require login. Please sign in.");
      // navigate("/sign-in");
      return;
    }
    if (!isLoggedIn && validationError) {
      setErrorMessage(validationError);
      return;
    }
    setShowPaymentModal(true);
  };

  const handlePaymentConfirm = async (method) => {
    setShowPaymentModal(false);

    try {
      if (isLoggedIn) {
        if (method === "wallet") {
          await handleWalletOrder();
        } else if (method === "stripe") {
          await handleUserStripeOrder();
        } else {
          await handleGuestOrder();
        }
      } else {
        await handleGuestOrder();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleApplyPromocode = async (code) => {
    try {
      setPromocodeError("");

      const normalizedCode = (code || "").trim();
      if (!normalizedCode) {
        setPromocodeData(null);
        setPromocodeError("Invalid coupon");
        return null;
      }

      console.log("🔍 Applying promocode:", normalizedCode);
      const data = await validateVoucherApi(normalizedCode);
      console.log("📋 Promocode response:", data);

      if (data?.available && data?.promocode?.type === "sell_discount") {
        const promo = {
          code: data.promocode.code,
          type: data.promocode.type,
          amount: data.promocode.amount,
        };

        setPromocodeData(promo);
        setPromocodeError("");
        return data;
      }

      setPromocodeData(null);
      setPromocodeError("Invalid coupon");
      return data;
    } catch (error) {
      console.error("❌ Promocode validation failed:", error);
      setPromocodeData(null);
      setPromocodeError("Invalid coupon");
      return null;
    }
  };
  const handleEsimConfirm = (checked) => {
    setEsimConfirmed(checked);

    if (checked && !esimConfirmed) {
      setShowEsimModal(true);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const loadPackages = async () => {
      console.log("🚀 START loadPackages, packageIds:", packageIds);

      if (!packageIds || packageIds.length === 0) {
        setPackages([]);
        return;
      }

      try {
        const results = await Promise.all(
          packageIds.map(async (pid) => {
            try {
              const data = await fetchPackageById(pid, walletCurrency);
              return data;
            } catch {
              const cartItem = displayItems.find((it) => it.internal_package_id || it.id === String(pid));
              console.log("CART ITEM", cartItem);
              if (!cartItem) return null;

              return {
                id: cartItem.internal_package_id || cartItem.id,
                name: cartItem.name,
                price: cartItem.price,
                currency: walletCurrency,
                country_id: cartItem.country_id,
                region_id: cartItem.region_id,
                gigabytes: cartItem.gigabytes,
                duration: cartItem.duration,
                top_up_available: cartItem.top_up_available,
              };
            }
          }),
        );

        const next = results.filter(Boolean);

        if (!cancelled) setPackages(next);
      } catch (e) {
        if (!cancelled) setPackages([]);
      }
    };

    loadPackages();

    return () => {
      cancelled = true;
    };
  }, [packageIds, walletCurrency]);

  const totals = useMemo(() => {
    const subtotal = (displayItems || []).reduce((acc, cartItem) => {
      const pid = cartItem.internal_package_id || cartItem.id;
      const pkg = packages.find((p) => String(p.id) === pid);
      if (!pkg) return acc;
      const qty = cartItem.quantity || 1;
      return acc + (pkg.price || 0) * qty;
    }, 0);

    const taxes = subtotal * 0.0;
    return { subtotal, taxes, total: subtotal + taxes };
  }, [displayItems, packages]);

  if (authLoading) return null;

  return (
    <section className="checkout">
      {isLoggedIn ? (
        <div className="checkout__center-column">
          <OrderSummary
            packages={packages}
            currency={currency}
            totals={totals}
            items={displayItems}
            onPlaceOrder={handlePlaceOrder}
            promocodeData={promocodeData}
            onApplyPromocode={handleApplyPromocode}
            hidePromocode={isTopUpOrder}
            newsletterOptIn={newsletterOptIn}
            onNewsletterOptInChange={setNewsletterOptIn}
            showNewsletterCheckbox={showNewsletterCheckbox}
            esimConfirmed={esimConfirmed}
            onEsimConfirmChange={handleEsimConfirm}
            speedConfirmed={speedConfirmed}
            promocodeError={promocodeError}
            onSpeedConfirmChange={setSpeedConfirmed}
          />
        </div>
      ) : (
        <>
          <div className="checkout__left-column">
            <AuthPanel
              fullName={fullName}
              email={email}
              errorMessage={errorMessage}
              onFullNameChange={(e) => setFullName(e.target.value)}
              onEmailChange={(e) => setEmail(e.target.value)}
              onErrorClose={() => setErrorMessage("")}
              onLoginClick={() => navigate("/sign-in")}
            />
          </div>
          <div className="checkout__right-column">
            <OrderSummary
              packages={packages}
              currency={currency}
              totals={totals}
              items={displayItems}
              orderLoading={orderLoading}
              onPlaceOrder={handlePlaceOrder}
              newsletterOptIn={newsletterOptInGuest}
              onNewsletterOptInChange={setNewsletterOptInGuest}
              showNewsletterCheckbox={true}
              esimConfirmed={esimConfirmed}
              onEsimConfirmChange={handleEsimConfirm}
              speedConfirmed={speedConfirmed}
              onSpeedConfirmChange={setSpeedConfirmed}
              isGuest={true}
            />
          </div>
        </>
      )}

      <PaymentModal
        isOpen={showPaymentModal}
        type={status}
        onClose={() => setShowPaymentModal(false)}
        onConfirm={handlePaymentConfirm}
        wallet={wallet}
        loading={orderLoading}
        onlyWallet={isTopUpOrder}
      />

      <EsimCheckModal isOpen={showEsimModal} onClose={() => setShowEsimModal(false)} />

      <StatusModal
        isOpen={showStatusModal}
        type={finalStatus}
        isLoggedIn={isLoggedIn}
        customMessage={errorMessage}
        onClose={() => {
          setShowStatusModal(false);
          setFinalStatus(null);
          setErrorMessage("");
        }}
        onSuccessAction={() => {
          clear();
          setShowStatusModal(false);
          setFinalStatus(null);
          setErrorMessage("");

          if (isLoggedIn) {
            navigate("/esim");
          } else {
            navigate("/shop");
          }
        }}
      />
    </section>
  );
};

export default CheckoutLayout;
